import React,{useEffect,useMemo,useState}from"react";
import{Box,Button,FormControl,InputLabel,MenuItem,Paper,Select,Table,TableBody,TableCell,TableContainer,TableHead,TablePagination,TableRow,TextField,Typography,useMediaQuery}from"@mui/material";
import{useNavigate}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAlert}from"../../context/AlertContext";
import NewMovement from"./AdminInventory/NewMovement";

function formatDate(value){
  if(!value)return"-";
  return new Date(value).toLocaleDateString("es-EC",{
    year:"numeric",
    month:"2-digit",
    day:"2-digit"
  });
}

export default function AdminInventory(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showError}=useAlert();
  const navigate=useNavigate();
  const isMobile=useMediaQuery("(max-width:900px)");

  const[movements,setMovements]=useState([]);
  const[search,setSearch]=useState("");
  const[typeFilter,setTypeFilter]=useState("todos");
  const[originFilter,setOriginFilter]=useState("todos");
  const[loading,setLoading]=useState(false);
  const[empresaLoading,setEmpresaLoading]=useState(true);
  const[empresaId,setEmpresaId]=useState("");
  const[page,setPage]=useState(0);
  const[canCreate,setCanCreate]=useState(false);
  const[newMovementOpen,setNewMovementOpen]=useState(false);

  const rowsPerPage=isMobile?10:30;
  const token=localStorage.getItem("accessToken");

  const loadEmpresa=async()=>{
    try{
      setEmpresaLoading(true);

      const response=await fetch(
        "/api/enterprise/empresa/obtener/",
        {
          headers:{
            Authorization:`Bearer ${token}`
          }
        }
      );

      const data=await response.json();

      if(!response.ok){
        showError(
          data.error||
          data.detail||
          "No se pudo cargar la empresa."
        );
        return;
      }

      if(!data?.empresa?.id){
        showError("No existe una empresa configurada.");
        return;
      }

      setEmpresaId(data.empresa.id);
    }catch{
      showError("Error de conexión al cargar la empresa.");
    }finally{
      setEmpresaLoading(false);
    }
  };

  const loadMovements=async()=>{
    if(!empresaId)return;

    try{
      setLoading(true);

      const params=new URLSearchParams({
        empresa_id:empresaId
      });

      if(search.trim()){
        params.append("q",search.trim());
      }

      if(typeFilter!=="todos"){
        params.append(
          "movement_type",
          typeFilter
        );
      }

      if(originFilter!=="todos"){
        params.append(
          "origin",
          originFilter
        );
      }

      const response=await fetch(
        `/api/inventory/movements/?${params.toString()}`,
        {
          headers:{
            Authorization:`Bearer ${token}`
          }
        }
      );

      const data=await response.json();

      if(!response.ok){
        showError(
          data.error||
          data.detail||
          "No se pudieron cargar los movimientos."
        );
        setMovements([]);
        return;
      }

      setMovements(
        Array.isArray(data.results)
          ?data.results
          :[]
      );

      setCanCreate(!!data.can_create);
    }catch{
      setMovements([]);
      showError("Error de conexión.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadEmpresa();
  },[]);

  useEffect(()=>{
    if(!empresaId)return;

    const delay=setTimeout(()=>{
      setPage(0);
      loadMovements();
    },350);

    return()=>clearTimeout(delay);
  },[
    search,
    typeFilter,
    originFilter,
    empresaId
  ]);

  useEffect(()=>{
    setPage(0);
  },[rowsPerPage]);

  const groupedMovements=useMemo(()=>{
    const seen=new Set();
    const result=[];

    movements.forEach(item=>{
      if(item.detail_available&&item.movement_number){
        const key=`batch-${item.movement_number}`;

        if(seen.has(key)){
          return;
        }

        seen.add(key);
        result.push(item);
        return;
      }

      result.push(item);
    });

    return result;
  },[movements]);

  const visibleMovements=useMemo(()=>{
    const start=page*rowsPerPage;

    return groupedMovements.slice(
      start,
      start+rowsPerPage
    );
  },[
    groupedMovements,
    page,
    rowsPerPage
  ]);

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none",
        "& .MuiMenu-list":{
          p:.5
        },
        "& .MuiMenuItem-root":{
          fontSize:14,
          borderRadius:"7px",
          color:dashboardTheme.text_color,
          "&:hover":{
            bgcolor:dashboardTheme.dashboard_background
          },
          "&.Mui-selected":{
            bgcolor:dashboardTheme.dashboard_background
          }
        }
      }
    }
  };

  const selectSx={
    minWidth:{
      xs:"100%",
      sm:170
    },
    ...dashboardTheme.inputStyles,
    "& .MuiSelect-icon":{
      color:dashboardTheme.text_color
    }
  };

  if(newMovementOpen){
    return(
      <NewMovement
        empresaId={empresaId}
        onBack={()=>
          setNewMovementOpen(false)
        }
        onCreated={data=>{
          setNewMovementOpen(false);

          const number=
            data?.movement?.number||
            "";

          if(number){
            navigate(
              `/admin-panel/inventario/${encodeURIComponent(number)}`
            );
          }else{
            loadMovements();
          }
        }}
      />
    );
  }

  return(
    <Box sx={{
      minHeight:"100vh",
      bgcolor:dashboardTheme.dashboard_background,
      display:"flex",
      color:dashboardTheme.text_color,
      overflowX:"hidden"
    }}>
      <Sidebar
        open={sidebarOpen}
        onClose={()=>
          setSidebarOpen(false)
        }
      />

      <Box sx={{
        flex:1,
        minWidth:0,
        minHeight:"100vh",
        display:"flex",
        flexDirection:"column"
      }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={()=>
            setSidebarOpen(prev=>!prev)
          }
        />

        <Box
          component="main"
          sx={{
            flex:1,
            width:"100%",
            p:{
              xs:1.5,
              sm:2,
              md:3
            }
          }}
        >
          <Box sx={{
            width:{
              xs:"100%",
              lg:"95%"
            },
            mx:"auto",
            mt:{
              xs:1,
              md:1.5
            }
          }}>
            <Box sx={{
              display:"flex",
              alignItems:{
                xs:"stretch",
                sm:"center"
              },
              justifyContent:"space-between",
              flexDirection:{
                xs:"column",
                sm:"row"
              },
              gap:1.5,
              mb:2
            }}>
              <Box>
                <Typography sx={{
                  fontSize:{
                    xs:24,
                    sm:26,
                    md:30
                  },
                  fontWeight:600,
                  color:dashboardTheme.primary_color,
                  lineHeight:1.2
                }}>
                  Movimientos de inventario
                </Typography>

                <Typography sx={{
                  fontSize:13.5,
                  color:dashboardTheme.muted_text_color,
                  mt:.45
                }}>
                  Historial de ingresos, salidas y ajustes de existencias.
                </Typography>
              </Box>

              {canCreate&&(
                <Button
                  variant="contained"
                  disabled={
                    empresaLoading||
                    !empresaId
                  }
                  onClick={()=>
                    setNewMovementOpen(true)
                  }
                  sx={
                    dashboardTheme.buttonStyles.contained
                  }
                >
                  Registrar movimiento
                </Button>
              )}
            </Box>

            <Paper
              elevation={0}
              sx={dashboardTheme.tableStyles.wrapper}
            >
              <Box sx={{
                ...dashboardTheme.tableStyles.toolbar,
                display:"flex",
                flexWrap:"wrap",
                alignItems:"center",
                gap:1
              }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar movimiento, usuario o motivo"
                  value={search}
                  onChange={event=>
                    setSearch(event.target.value)
                  }
                  sx={{
                    maxWidth:{
                      xs:"100%",
                      md:430
                    },
                    ...dashboardTheme.inputStyles
                  }}
                />

                <FormControl
                  size="small"
                  sx={selectSx}
                >
                  <InputLabel>
                    Tipo
                  </InputLabel>

                  <Select
                    label="Tipo"
                    value={typeFilter}
                    onChange={event=>
                      setTypeFilter(event.target.value)
                    }
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="todos">
                      Todos
                    </MenuItem>

                    <MenuItem value="ENTRADA">
                      Entrada
                    </MenuItem>

                    <MenuItem value="SALIDA">
                      Salida
                    </MenuItem>

                    <MenuItem value="AJUSTE_POSITIVO">
                      Ajuste positivo
                    </MenuItem>

                    <MenuItem value="AJUSTE_NEGATIVO">
                      Ajuste negativo
                    </MenuItem>

                    <MenuItem value="REVERSO_ENTRADA">
                      Reverso entrada
                    </MenuItem>

                    <MenuItem value="REVERSO_SALIDA">
                      Reverso salida
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={selectSx}
                >
                  <InputLabel>
                    Origen
                  </InputLabel>

                  <Select
                    label="Origen"
                    value={originFilter}
                    onChange={event=>
                      setOriginFilter(event.target.value)
                    }
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="todos">
                      Todos
                    </MenuItem>

                    <MenuItem value="COMPRA">
                      Compra
                    </MenuItem>

                    <MenuItem value="VENTA">
                      Venta
                    </MenuItem>

                    <MenuItem value="AJUSTE_MANUAL">
                      Ajuste manual
                    </MenuItem>

                    <MenuItem value="CARGA_INICIAL">
                      Carga inicial
                    </MenuItem>

                    <MenuItem value="ANULACION_VENTA">
                      Anulación de venta
                    </MenuItem>

                    <MenuItem value="ANULACION_COMPRA">
                      Anulación de compra
                    </MenuItem>

                    <MenuItem value="DEVOLUCION">
                      Devolución
                    </MenuItem>

                    <MenuItem value="OTRO">
                      Otro
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={
                dashboardTheme.tableStyles.tableArea
              }>
                <TableContainer sx={
                  dashboardTheme.tableStyles.container
                }>
                  <Table
                    size="small"
                    sx={dashboardTheme.tableStyles.table}
                  >
                    <TableHead>
                      <TableRow sx={
                        dashboardTheme.tableStyles.headRow
                      }>
                        <TableCell sx={
                          dashboardTheme.tableStyles.headCell
                        }>
                          Fecha
                        </TableCell>

                        <TableCell sx={{
                          ...dashboardTheme.tableStyles.headCell,
                          minWidth:150
                        }}>
                          Documento
                        </TableCell>

                        <TableCell sx={
                          dashboardTheme.tableStyles.headCell
                        }>
                          Tipo
                        </TableCell>

                        <TableCell sx={
                          dashboardTheme.tableStyles.headCell
                        }>
                          Origen
                        </TableCell>

                        <TableCell sx={{
                          ...dashboardTheme.tableStyles.headCell,
                          minWidth:260
                        }}>
                          Motivo
                        </TableCell>

                        <TableCell sx={{
                          ...dashboardTheme.tableStyles.headCell,
                          minWidth:160
                        }}>
                          Usuario
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visibleMovements.map(movement=>(
                        <TableRow
                          key={
                            movement.movement_number
                              ?`batch-${movement.movement_number}`
                              :`movement-${movement.id}`
                          }
                          sx={
                            dashboardTheme.tableStyles.bodyRow
                          }
                        >
                          <TableCell sx={{
                            whiteSpace:"nowrap",
                            color:dashboardTheme.text_color
                          }}>
                            {formatDate(
                              movement.created_at
                            )}
                          </TableCell>

                          <TableCell>
                            {movement.detail_available&&
                            movement.movement_number?(
                              <Button
                                variant="text"
                                onClick={()=>
                                  navigate(
                                    `/admin-panel/inventario/${encodeURIComponent(movement.movement_number)}`
                                  )
                                }
                                sx={{
                                  p:0,
                                  minWidth:0,
                                  textTransform:"none",
                                  fontSize:13.5,
                                  fontWeight:700,
                                  color:dashboardTheme.primary_color,
                                  textDecoration:"underline",
                                  textUnderlineOffset:"3px",
                                  "&:hover":{
                                    bgcolor:"transparent",
                                    textDecoration:"underline"
                                  }
                                }}
                              >
                                {movement.movement_number}
                              </Button>
                            ):(
                              <Typography sx={{
                                fontSize:13.5,
                                color:
                                  dashboardTheme.muted_text_color
                              }}>
                                {movement.reference_number||"-"}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell sx={{
                            whiteSpace:"nowrap",
                            color:dashboardTheme.text_color
                          }}>
                            {movement.movement_type_label||
                              movement.movement_type||
                              "-"}
                          </TableCell>

                          <TableCell sx={{
                            whiteSpace:"nowrap",
                            color:dashboardTheme.text_color
                          }}>
                            {movement.origin_label||
                              movement.origin||
                              "-"}
                          </TableCell>

                          <TableCell>
                            <Typography sx={{
                              fontSize:13.5,
                              color:dashboardTheme.text_color,
                              lineHeight:1.35
                            }}>
                              {movement.reason||"-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography sx={{
                              fontSize:13.5,
                              color:dashboardTheme.text_color
                            }}>
                              {movement.user?.name||
                                movement.user?.email||
                                "-"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}

                      {!loading&&
                      !empresaLoading&&
                      visibleMovements.length===0&&(
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={
                              dashboardTheme.tableStyles.emptyCell
                            }
                          >
                            No hay movimientos registrados
                          </TableCell>
                        </TableRow>
                      )}

                      {(loading||empresaLoading)&&(
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={
                              dashboardTheme.tableStyles.emptyCell
                            }
                          >
                            Cargando movimientos...
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={groupedMovements.length}
                page={page}
                onPageChange={(_event,newPage)=>
                  setPage(newPage)
                }
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({
                  from,
                  to,
                  count
                })=>`${from}-${to} de ${count}`}
                sx={
                  dashboardTheme.tableStyles.pagination
                }
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}